import React, { useEffect, useState } from "react";
import ModalShell from "../common/ModalShell";
import ui from "../../pages/dashboard/DashboardUI.module.css";
import CustomDropdown from "./CustomDropdown";

export default function FormModal({ 
  title, 
  fields, 
  onSubmit, 
  onCancel, 
  error, 
  tabs = null, 
  activeTab: controlledActiveTab = null,
  onTabChange = null,
  assignedEmployees = [], 
  setMilestones = () => {},
  milestones = [], 
  customContent = null,
  customTabContent = null,
  setAssignedEmployees = () => {}, 
  availableEmployees = [],
  onAddMilestoneClick = () => {}, 
  onMilestoneClick = () => {},
  employeeSearch = "", 
  setEmployeeSearch = () => {},
  footerActions = null,
  overviewReadOnly={},
  teamReadOnly = false, // New prop
  submitLabel = "Save",
  cancelLabel = "Cancel",
}) {
  const [focusedFieldIndex, setFocusedFieldIndex] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [internalActiveTab, setInternalActiveTab] = useState(
    tabs ? (Array.isArray(tabs) && tabs[0]?.key ? tabs[0].key : tabs[0]) : null
  );

  const activeTab = controlledActiveTab !== null ? controlledActiveTab : internalActiveTab;
  const handleTabChange = onTabChange || setInternalActiveTab;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const getSelectLabel = (field) => {
    if (!field.options || field.options.length === 0) return field.value;

    const match = field.options.find(opt =>
      typeof opt === "object"
        ? opt.value === field.value
        : opt === field.value
    );

    return typeof match === "object" ? match.label : match || field.value;
  };


  const renderField = ({
    label,
    value,
    checked, // Add checked prop for checkboxes
    onChange,
    type = "text",
    step,
    min,
    placeholder,
    options,
    suggestions,
    onSuggestionClick,
    render,
    disabled = false,
    accept,
    note,
  }, idx) => {
    // Handle custom render function
    if (type === "custom" && render) {
      return (
        <div className={ui.inputGroup} key={idx}>
          {label && <label>{label}:</label>}
          {render()}
        </div>
      );
    }

    // Handle divider/separator
    if (type === "divider") {
      return (
        <div key={idx} style={{ 
          margin: "1.5rem 0 1rem 0", 
          paddingTop: "1rem", 
          borderTop: "1px solid var(--border-subtle)" 
        }}>
          {label && (
            <h4 style={{ 
              margin: "0 0 1rem 0", 
              fontSize: "0.95rem", 
              fontWeight: "600",
              color: "var(--text-muted)" 
            }}>
              {label}
            </h4>
          )}
        </div>
      );
    }

    // Handle checkbox type
    if (type === "checkbox") {
      return (
        <div className={ui.inputGroup} key={idx} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <input
            type="checkbox"
            checked={checked ?? false}
            onChange={onChange}
            disabled={disabled}
            style={{ width: "auto", margin: 0 }}
          />
          <label style={{ margin: 0 }}>{label}</label>
        </div>
      );
    }

    // REST IS UNCHANGED - exact same as before
    return (
      <div className={ui.inputGroup} key={idx} style={{ position: "relative" }}>
        <label>{label}:</label>
        {type === "textarea" ? (
          <textarea 
            value={value ?? ""} 
            disabled={disabled}
            onChange={onChange} />
        ) : type === "select" ? (
          <CustomDropdown
            fullWidth
            value={value ?? ""}
            disabled={disabled}
            onChange={(val) => onChange({ target: { value: val } })}
            options={options.map((opt) =>
              typeof opt === "object"
                ? { value: String(opt.value), label: opt.label }
                : { value: String(opt), label: String(opt) }
            )}
          />
        ) : (
          <>
            <input
              type={type}
              {...(type === "file" ? {} : { value: value ?? "" })}
              onChange={onChange}
              step={step}
              min={min}
              disabled={disabled}
              placeholder={placeholder}
              accept={accept}
              autoComplete="off"
              onFocus={() => setFocusedFieldIndex(idx)}
              onBlur={() => setFocusedFieldIndex(null)}
            />
            {focusedFieldIndex === idx && suggestions?.length > 0 && (
              <ul className={ui.autocompleteList}>
                {suggestions.map((sugg, i) => (
                  <li
                    key={i}
                    className={ui.autocompleteItem}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onSuggestionClick?.(sugg);
                    }}
                  >
                    {sugg}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {note && (
          <div style={{ 
            fontSize: "0.8rem", 
            color: "var(--text-muted)", 
            marginTop: "0.25rem" 
          }}>
            {note}
          </div>
        )}
      </div>
    );
  };

  const Root = onSubmit ? "form" : "div";

  return (
    <ModalShell onClose={onCancel} closeOnOverlayClick={false} overlayClassName={ui.modalOverlay}>
      {/* Without onSubmit it is a plain panel, so buttons of a nested manager do not submit it. */}
      <Root className={ui.modal} onSubmit={onSubmit}>
        <div className={ui.modalHeader}>
          <h2>{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className={ui.closeButton}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Tab Buttons */}
        {tabs && tabs.length > 0 && (
          <div className={ui.modalTabs}>
            {tabs.map((tab) => {
              // Handle both string tabs (from Projects.js) and object tabs (from Leads.js)
              const tabKey = typeof tab === 'string' ? tab : tab.key;
              const tabLabel = typeof tab === 'string' ? tab : tab.label;
              
              return (
                <button
                  key={tabKey}
                  type="button"
                  className={`${ui.tabButton} ${
                    (typeof tab === 'string' ? activeTab === tab : activeTab === tab.key)
                      ? ui.activeTab 
                      : ""
                  }`}
                  onClick={() => handleTabChange(tabKey)}
                >
                  {tabLabel}
                </button>
              );
            })}
          </div>
        )}

        <div className={ui.modalContent}>
          {/* If custom tab content is provided, render it instead of default content */}
          {customTabContent ? (
            customTabContent
          ) : (
            <>
              {/* No tabs or tabs with custom handling: show fields */}
              {(!tabs || (tabs && !["team", "milestones", "overview"].includes(activeTab?.toLowerCase()))) && 
                fields.map((field, idx) => (
                  <React.Fragment key={field.label || idx}>
                    {field.type === "row" && field.fields ? (
                      <div className={ui.inputRow}>
                        {field.fields.map((subField, subIdx) => 
                          renderField(subField, `${idx}-${subIdx}`)
                        )}
                      </div>
                    ) : (
                      renderField(field, idx)
                    )}
                    {field.label === "To" && customContent && (
                      <div className={ui.customContent}>
                        {customContent}
                      </div>
                    )}
                  </React.Fragment>
                ))
              }

              {/* Tab: Overview */}
              {tabs && activeTab?.toLowerCase() === "overview" && (
                <>
                  {overviewReadOnly
                    ? fields
                        .filter(f => f.section === "overview")
                        .map((f, idx) => (
                          <div key={idx} className={ui.readOnlyRow}>
                            <label className={ui.readOnlyLabel}>{f.label}</label>
                            
                            <div className={ui.readOnlyValue}>
                              {f.type === "select"
                                ? getSelectLabel(f)
                                : (f.value || "—")}
                            </div>
                          </div>
                        ))
                    : fields
                        .filter(f => f.section === "overview")
                        .map(renderField)
                  }
                </>
              )}

              {/* Tab: Team */}
              {tabs && activeTab?.toLowerCase() === "team" && (
                <div>
                  {!teamReadOnly && (
                    <>
                      <div className={ui.inputGroup}>
                        <label>Search people by name or job title</label>
                        <input
                          type="text"
                          placeholder="Search..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          onFocus={() => setSearchFocused(true)}
                          onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                        />
                      </div>

                      {searchFocused && (
                        <div className={ui.searchResults}>
                          {availableEmployees
                            .filter(emp =>
                              (emp.name + emp.job_title).toLowerCase().includes(employeeSearch.toLowerCase())
                            )
                            .filter(emp => !assignedEmployees.some(a => a.id === emp.id))
                            .map(emp => (
                              <div
                                key={emp.id}
                                className={ui.searchItem}
                                onClick={() => {
                                  setAssignedEmployees(prev => [...prev, { ...emp, role: "" }]);
                                  setEmployeeSearch("");
                                }}
                              >
                                {emp.name} — {emp.job_title}
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}

                  {assignedEmployees.length > 0 ? (
                    <div className={ui.assignedList}>
                      <h4>Assigned Team Members</h4>
                      {assignedEmployees.map((emp, idx) => (
                        <div key={emp.id} className={ui.assignedItem}>
                          <div className={teamReadOnly ? ui.employeeInfoReadOnly : ui.employeeInfo}>
                            <span className={ui.employeeName}>{emp.name}</span>
                            <span className={ui.employeeTitle}>{emp.job_title}</span>
                          </div>
                          {teamReadOnly ? (
                            <span className={ui.employeeRole}>{emp.role || "—"}</span>
                          ) : (
                            <>
                              <input
                                type="text"
                                placeholder="Role in project"
                                value={emp.role || ""}
                                onChange={(e) => {
                                  const updated = [...assignedEmployees];
                                  updated[idx].role = e.target.value;
                                  setAssignedEmployees(updated);
                                }}
                                className={ui.roleInput}
                              />
                              <button
                                type="button"
                                className={ui.removeButton}
                                onClick={() => {
                                  setAssignedEmployees(assignedEmployees.filter(e => e.id !== emp.id));
                                }}
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={ui.emptyState}>
                      <p>No team members assigned yet</p>
                    </div>
                  )}
                </div>
              )}
              
              {tabs && activeTab?.toLowerCase() === "milestones" && (
                <div className={ui.inputGroup}>
                  <button
                    type="button"
                    className={ui.saveButton}
                    onClick={onAddMilestoneClick}
                  >
                    Add New Milestone
                  </button>

                  {milestones.length > 0 ? (
                    <div className={ui.milestoneListContainer}>
                      {milestones.map((m) => {
                        // Calculate progress percentage
                        const startDate = m.start_date ? new Date(m.start_date) : null;
                        const dueDate = m.due_date ? new Date(m.due_date) : null;
                        const today = new Date();
                        
                        let progressPercent = 0;
                        if (startDate && dueDate) {
                          const totalDuration = dueDate - startDate;
                          const elapsed = today - startDate;
                          if (totalDuration > 0) {
                            progressPercent = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
                          }
                          if (today >= dueDate) progressPercent = 100;
                          if (today <= startDate) progressPercent = 0;
                        }

                        const formatDate = (dateStr) => {
                          if (!dateStr) return "—";
                          return new Date(dateStr).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          });
                        };

                        const getStatusClass = (status) => {
                          const s = status?.toLowerCase().replace(/\s+/g, '_');
                          return ui[`milestone_${s}`] || '';
                        };

                        return (
                          <div
                            key={m.milestone_id || m.temp_id}
                            className={ui.milestoneCardEnhanced}
                            onClick={() => onMilestoneClick(m)}
                          >
                            <div className={ui.milestoneCardHeader}>
                              <div className={ui.milestoneCardInfo}>
                                <span className={ui.milestoneCardName}>{m.milestone_name}</span>
                                <span className={`${ui.milestoneCardStatus} ${getStatusClass(m.status)}`}>
                                  {m.status || 'open'}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={ui.removeButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMilestones(prev =>
                                    prev.filter(ms =>
                                      ms.milestone_id
                                        ? ms.milestone_id !== m.milestone_id
                                        : ms.temp_id !== m.temp_id
                                    )
                                  );
                                }}
                              >
                                ✕
                              </button>
                            </div>
                            
                            {(startDate || dueDate) && (
                              <div className={ui.milestoneCardDates}>
                                <span>{formatDate(m.start_date)}</span>
                                <span className={ui.milestoneDateSeparator}>→</span>
                                <span>{formatDate(m.due_date)}</span>
                              </div>
                            )}
                            
                            {startDate && dueDate && (
                              <div className={ui.milestoneProgressContainer}>
                                <div className={ui.milestoneProgressTrack}>
                                  <div 
                                    className={`${ui.milestoneProgressFill} ${
                                      m.status?.toLowerCase() === 'completed' ? ui.progressComplete :
                                      progressPercent >= 100 ? ui.progressOverdue : ''
                                    }`}
                                    style={{ width: `${m.status?.toLowerCase() === 'completed' ? 100 : progressPercent}%` }}
                                  />
                                </div>
                                <span className={ui.milestoneProgressLabel}>
                                  {m.status?.toLowerCase() === 'completed' ? 'Complete' : `${Math.round(progressPercent)}%`}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className={ui.milestoneEmptyText}>
                      No milestones added yet.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {error && <p className={ui.error}>{error}</p>}
        <div className={ui.actions} style={{ alignItems: "center" }}>
          {footerActions && (
            <div style={{ marginRight: "auto" }}>
              {footerActions}
            </div>
          )}
          <button type="button" className={ui.cancelButton} onClick={onCancel}>{cancelLabel}</button>
          {onSubmit && !(overviewReadOnly === true || teamReadOnly === true) && (
            <button type="submit" className={ui.saveButton}>{submitLabel}</button>
          )}
        </div>
      </Root>
    </ModalShell>
  );
}
